import { useCallback, useState } from 'react'

/**
 * Shared Weekly / Monthly period control for dashboard-style pages.
 *
 * "Weekly" = the rolling **last 7 days**, "Monthly" = the rolling **last
 * 30 days**. The choice is the SAME everywhere and persisted per browser
 * (localStorage) so it sticks across sessions and pages — the goal being
 * one consistent period filter across every panel. The period only drives
 * *activity* metrics (Done, Reports, Points, AI usage, …); live-snapshot
 * numbers (open backlog, member counts) ignore it.
 */
const STORAGE_KEY = 'taskmagix.dashboardPeriod'
const PERIODS = ['weekly', 'monthly']

/** Number of days a period spans (rolling window from now). */
export function periodDays(period) {
  return period === 'monthly' ? 30 : 7
}

/** Short caption for column sub-headers / card captions ("7 days"). */
export function periodLabel(period) {
  return period === 'monthly' ? '30 days' : '7 days'
}

/** Persisted Weekly/Monthly preference. Returns `[period, setPeriod]`.
 *  Defaults to 'weekly'. Safe if localStorage is unavailable. */
export function useDashboardPeriod() {
  const [period, setPeriodState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return PERIODS.includes(stored) ? stored : 'weekly'
    } catch {
      return 'weekly'
    }
  })
  const setPeriod = useCallback((next) => {
    const value = PERIODS.includes(next) ? next : 'weekly'
    setPeriodState(value)
    try {
      localStorage.setItem(STORAGE_KEY, value)
    } catch {
      /* ignore — non-persistent is fine */
    }
  }, [])
  return [period, setPeriod]
}

/** Segmented Weekly | Monthly toggle. */
export function PeriodToggle({ value, onChange, className = '' }) {
  return (
    <div
      role="group"
      aria-label="Period"
      className={
        'inline-flex shrink-0 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5 ' +
        className
      }
    >
      {[
        { key: 'weekly', label: 'Weekly' },
        { key: 'monthly', label: 'Monthly' },
      ].map((p) => {
        const active = value === p.key
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(p.key)}
            aria-pressed={active}
            className={
              'px-3 h-8 rounded-md text-sm font-medium cursor-pointer transition-colors ' +
              (active
                ? 'bg-[color:var(--color-accent)] text-white'
                : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
            }
          >
            {p.label}
          </button>
        )
      })}
    </div>
  )
}
